import { DB_NAME, DB_VERSION, STORE_NAMES } from './schema';

/**
 * IndexedDBクライアント
 */
export class IndexedDBClient {
  private db: IDBDatabase | null = null;
  private openPromise: Promise<IDBDatabase> | null = null;

  /**
   * データベースを開く
   */
  async open(): Promise<IDBDatabase> {
    // SSR環境チェック
    if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
      throw new Error('IndexedDBはブラウザ環境でのみ利用可能です');
    }

    // 既に開いている場合は即座に返す
    if (this.db) {
      return this.db;
    }

    // 既に開いている最中の場合は、そのPromiseを返す（競合を防ぐ）
    if (this.openPromise) {
      return this.openPromise;
    }

    // 新しいPromiseを作成して保持
    this.openPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        this.openPromise = null; // エラー時はリセット
        reject(
          new Error(
            `IndexedDBを開けませんでした: ${request.error?.message || '不明なエラー'}`
          )
        );
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        // Version 1: 初期スキーマ作成
        if (oldVersion < 1) {
          // learningProgress ストア: 学習進捗を保存
          if (!db.objectStoreNames.contains(STORE_NAMES.LEARNING_PROGRESS)) {
            const learningProgressStore = db.createObjectStore(
              STORE_NAMES.LEARNING_PROGRESS,
              {
                keyPath: 'id',
              }
            );
            // courseId でのインデックス（基礎編/アドバンスト編の検索用）
            learningProgressStore.createIndex('courseId', 'courseId', {
              unique: false,
            });
          }

          // learnerProfile ストア: 受講者プロファイル（アドバンスト編受講希望など）
          // out-of-line keys を使用（固定キー 'default' で保存）
          if (!db.objectStoreNames.contains(STORE_NAMES.LEARNER_PROFILE)) {
            db.createObjectStore(STORE_NAMES.LEARNER_PROFILE);
          }
        }
      };
    });

    return this.openPromise;
  }

  /**
   * データベースを閉じる
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  /**
   * トランザクションを開始
   */
  async transaction(
    storeName: string,
    mode: IDBTransactionMode
  ): Promise<IDBObjectStore> {
    const db = await this.open();
    const transaction = db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }
}

// シングルトンインスタンス
export const indexedDBClient = new IndexedDBClient();
